<form [formGroup]="form" (ngSubmit)="onSubmit()" novalidate>
  <div class="bb-form-body">

    <div class="bb-form-section">Book Details</div>
    <div class="row g-2">
      <div class="col-md-5">
        <label class="form-label">Book Name <span class="text-danger">*</span></label>
        <div class="bb-input-check-wrap">
          <input type="text" formControlName="bookName" class="form-control"
                 [class.is-invalid]="isFieldInvalid('bookName')"
                 placeholder="Enter full book title"
                 (input)="onBookNameInput()"
                 (blur)="onBookNameBlur()" />
          <span class="bb-input-spinner" *ngIf="isDuplicateChecking">
            <span class="spinner-border spinner-border-sm text-secondary"></span>
          </span>
        </div>
        <div class="invalid-feedback">
          <span *ngIf="f['bookName'].errors?.['required']">Book name is required.</span>
          <span *ngIf="f['bookName'].errors?.['minlength']">Min 5 characters.</span>
          <span *ngIf="f['bookName'].errors?.['maxlength']">Max 200 characters.</span>
        </div>
      </div>
      <div class="col-md-4">
        <label class="form-label d-flex align-items-center justify-content-between">
          <span>English Name</span>
          <button type="button" class="bb-copy-name-btn" (click)="copyBookNameToEnglish()"
                  title="Copy book name as-is into this field">
            <i class="pe-7s-copy-file me-1"></i>Copy
          </button>
        </label>
        <input type="text" formControlName="bookNameEnglish" class="form-control"
               placeholder="English name or transliteration"
               (blur)="onEnglishNameBlur()" />
      </div>
      <div class="col-md-3">
        <label class="form-label">ISBN</label>
        <input type="text" formControlName="ISBN" class="form-control"
               [class.is-invalid]="isFieldInvalid('ISBN')" placeholder="10 or 13 digits" />
        <div class="invalid-feedback">
          <span *ngIf="f['ISBN'].errors?.['invalidISBN']">ISBN must be 10 or 13 digits.</span>
        </div>
      </div>
    </div>

    <!-- Duplicate warning -->
    <div class="bb-dup-panel" *ngIf="showDuplicates">
      <div class="bb-dup-header">
        <i class="pe-7s-attention me-1"></i>
        <span>{{ duplicates.length }} similar book{{ duplicates.length === 1 ? '' : 's' }} already in catalogue — check before saving</span>
        <button type="button" class="bb-dup-dismiss" (click)="dismissDuplicates()">
          <i class="pe-7s-close"></i>
        </button>
      </div>
      <div class="bb-dup-list">
        <div class="bb-dup-item" *ngFor="let b of duplicates">
          <span class="bb-dup-id" *ngIf="b.bookId">{{ b.bookId }}</span>
          <span class="bb-dup-name">{{ b.bookName }}<ng-container *ngIf="b.bookNameEnglish && b.bookNameEnglish !== b.bookName"> / {{ b.bookNameEnglish }}</ng-container></span>
          <span class="bb-dup-author" *ngIf="b.authorNames?.length">{{ b.authorNames.join(', ') }}</span>
        </div>
      </div>
    </div>

    <div class="row g-2 mt-0">
      <div class="col-md-2">
        <label class="form-label">Year Published</label>
        <input type="number" formControlName="firstPublishedIn" class="form-control"
               [class.is-invalid]="isFieldInvalid('firstPublishedIn')"
               placeholder="e.g. 1997" min="1000" [max]="currentYear" />
        <div class="invalid-feedback">
          <span *ngIf="f['firstPublishedIn'].errors?.['invalidYear']">Enter a valid year.</span>
        </div>
      </div>
      <div class="col-md-2">
        <label class="form-label">Price (₹)</label>
        <input type="number" formControlName="price" class="form-control"
               [class.is-invalid]="isFieldInvalid('price')" placeholder="0.00" min="0" step="0.01" />
        <div class="invalid-feedback">Cannot be negative.</div>
      </div>
    </div>

    <div class="bb-form-section">Classification</div>
    <div class="bb-classif-row">
      <div class="bb-classif-col">
        <span class="bb-classif-group-label"><i class="pe-7s-user me-1"></i>Authors</span>
        <div class="bb-classif-chips" *ngIf="f['authorNames'].value?.length">
          <span class="bb-chip" *ngFor="let a of f['authorNames'].value">
            {{ a }}
            <button type="button" class="bb-chip-remove" (click)="removeAuthor(a)" title="Remove">×</button>
          </span>
        </div>
        <span class="bb-classif-empty" *ngIf="!f['authorNames'].value?.length">None selected</span>
        <button type="button" class="bb-classif-btn mt-2" (click)="openClassModal('authors')">
          <i class="pe-7s-edit me-1"></i>Select Authors
        </button>
      </div>
      <div class="bb-classif-sep"></div>
      <div class="bb-classif-col">
        <span class="bb-classif-group-label"><i class="pe-7s-bookmarks me-1"></i>Categories</span>
        <div class="bb-classif-chips" *ngIf="f['categoryNames'].value?.length">
          <span class="bb-chip" *ngFor="let c of f['categoryNames'].value">
            {{ c }}
            <button type="button" class="bb-chip-remove" (click)="removeCategory(c)" title="Remove">×</button>
          </span>
        </div>
        <span class="bb-classif-empty" *ngIf="!f['categoryNames'].value?.length">None selected</span>
        <button type="button" class="bb-classif-btn mt-2" (click)="openClassModal('categories')">
          <i class="pe-7s-edit me-1"></i>Select Categories
        </button>
      </div>
    </div>

    <div class="bb-form-section">Settings</div>
    <div class="row g-2 align-items-center">
      <div class="col-md-3" *ngIf="isEditMode">
        <label class="form-label">Copies in Field</label>
        <div>
          <span class="bb-copies-badge">
            <i class="pe-7s-map-marker" style="font-size:1rem;"></i>
            {{ f['copiesInField'].value ?? 0 }} issued
          </span>
        </div>
      </div>
      <div class="col-md-3 d-flex align-items-end pb-1">
        <div class="form-check form-switch bb-toggle-row">
          <input class="form-check-input" type="checkbox" role="switch"
                 id="isTranslated" formControlName="isTranslated" />
          <label class="form-check-label" for="isTranslated">
            {{ f['isTranslated'].value ? 'Translated' : 'Not translated' }}
          </label>
        </div>
      </div>
    </div>

    <ng-container *ngIf="f['isTranslated'].value">
      <div class="bb-form-section mt-1">Original Book Information</div>
      <div class="row g-2">
        <div class="col-md-5">
          <label class="form-label">Original Book Name <span class="text-danger">*</span></label>
          <input type="text" formControlName="originalBookName" class="form-control"
                 [class.is-invalid]="isFieldInvalid('originalBookName')"
                 placeholder="Title in original language" />
          <div class="invalid-feedback">Required for translated books.</div>
        </div>
        <div class="col-md-3">
          <label class="form-label">Original Language <span class="text-danger">*</span></label>
          <input type="text" formControlName="originalLanguage" class="form-control"
                 [class.is-invalid]="isFieldInvalid('originalLanguage')"
                 placeholder="e.g. English, Hindi" />
          <div class="invalid-feedback">Required for translated books.</div>
        </div>
      </div>
      <div class="row g-2 mt-0">
        <div class="col-md-8">
          <label class="form-label">Original Author(s)</label>
          <div class="bb-classif-chips mb-2" *ngIf="f['originalAuthorNames'].value?.length">
            <span class="bb-chip" *ngFor="let a of f['originalAuthorNames'].value">
              {{ a }}
              <button type="button" class="bb-chip-remove" (click)="removeOriginalAuthor(a)" title="Remove">×</button>
            </span>
          </div>
          <span class="bb-classif-empty d-block mb-2" *ngIf="!f['originalAuthorNames'].value?.length">None selected</span>
          <button type="button" class="bb-classif-btn" (click)="openClassModal('original-authors')">
            <i class="pe-7s-edit me-1"></i>Select Original Authors
          </button>
        </div>
      </div>
    </ng-container>

  </div>

  <div class="bb-form-footer">
    <div class="bb-form-footer__msg">
      <span *ngIf="errorMessage" class="text-danger"><i class="pe-7s-close-circle me-1"></i>{{ errorMessage }}</span>
      <span *ngIf="successMessage" class="text-success"><i class="pe-7s-check me-1"></i>{{ successMessage }}</span>
    </div>
    <button type="button" class="bb-btn-reset" *ngIf="!isEditMode" (click)="resetForm()">
      <i class="pe-7s-refresh"></i> Reset
    </button>
    <button type="button" class="bb-btn-save" [disabled]="isSaving || form.invalid" (click)="onSubmit()">
      <span *ngIf="!isSaving"><i class="pe-7s-diskette"></i> {{ isEditMode ? 'Update Book' : 'Save Book' }}</span>
      <span *ngIf="isSaving" class="d-flex align-items-center gap-1">
        <span class="spinner-border spinner-border-sm"></span> Saving...
      </span>
    </button>
  </div>
</form>

<!-- ── Classification Modal ───────────────────────────────────── -->
<div class="bb-classif-backdrop" *ngIf="showClassModal" (click)="closeClassModal()"></div>
<div class="bb-classif-modal" *ngIf="showClassModal" (click)="$event.stopPropagation()">

  <div class="bb-classif-modal-header">
    <div class="bb-classif-modal-title">
      <i class="pe-7s-user me-2 text-primary" *ngIf="classModalType !== 'categories'"></i>
      <i class="pe-7s-bookmarks me-2 text-primary" *ngIf="classModalType === 'categories'"></i>
      {{ classModalType === 'categories' ? 'Select Categories' : classModalType === 'original-authors' ? 'Select Original Authors' : 'Select Authors' }}
    </div>
    <button type="button" class="bm-ac-close" (click)="closeClassModal()">
      <i class="pe-7s-close"></i>
    </button>
  </div>

  <div class="bb-classif-modal-body">
    <app-multi-select *ngIf="classModalType === 'authors'"
      [options]="authorOptions" [(ngModel)]="classModalAuthors"
      [inline]="true" placeholder="Search authors..."
      [initialSearch]="lastAddedAuthorName">
    </app-multi-select>
    <app-multi-select *ngIf="classModalType === 'original-authors'"
      [options]="authorOptions" [(ngModel)]="classModalOriginalAuthors"
      [inline]="true" placeholder="Search original authors..."
      [initialSearch]="lastAddedAuthorName">
    </app-multi-select>
    <app-multi-select *ngIf="classModalType === 'categories'"
      [options]="categoryOptions" [(ngModel)]="classModalCategories"
      [inline]="true" placeholder="Search categories...">
    </app-multi-select>
  </div>

  <div class="bb-classif-modal-footer">
    <button type="button" class="bb-ia-add-author-btn"
            *ngIf="classModalType === 'authors' || classModalType === 'original-authors'"
            (click)="openAuthorOverlay()">
      <i class="pe-7s-plus me-1"></i>Add New Author
    </button>
    <div class="flex-grow-1"></div>
    <button type="button" class="btn btn-outline-secondary btn-sm" (click)="closeClassModal()">Cancel</button>
    <button type="button" class="btn btn-primary btn-sm" (click)="applyClassModal()">
      <i class="pe-7s-check me-1"></i>Apply
    </button>
  </div>

</div>

<!-- ── Add Author Overlay ─────────────────────────────────────── -->
<div class="bb-author-overlay-backdrop" *ngIf="showAuthorOverlay"></div>
<div class="bb-author-overlay-panel" *ngIf="showAuthorOverlay">
  <app-author-master [overlayMode]="true"
                     (authorCreated)="onAuthorCreated($event)"
                     (overlayDismiss)="closeAuthorOverlay()">
  </app-author-master>
</div>
